import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LinkIcon from '@mui/icons-material/Link';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonIcon from '@mui/icons-material/Person';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { useIsViewer } from 'root/src/client/v2/auth/hooks/useIsViewer';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { useIsAgency } from 'root/src/client/v2/auth/hooks/useIsAgency';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { ConnectedChannelsPanel } from './ConnectedChannelsPanel';
import { PaymentsPanel } from './PaymentsPanel';
import { AccountPanel } from './AccountPanel';
import { GamePlatformsPanel } from './GamePlatformsPanel';
import { OBSPanel } from './OBSPanel';
import { PublisherPanel } from './PublisherPanel';
import { AgencyPanel } from './AgencyPanel';
import { AdminPanel } from './AdminPanel';
const overlayClassName = 'fixed inset-0 z-[1100] bg-black/80 '
    + 'data-[state=open]:animate-dialog-overlay-show data-[state=closed]:animate-dialog-overlay-hide';
// Base (all breakpoints): position, glass, open/close animation, and the size bits shared by both
// the card and the expanded variant. `transition-[max-width,height]` animates the maximize toggle.
const contentBaseClassName = 'redesign fixed left-1/2 top-1/2 z-[1101] flex h-full w-full -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden '
    + 'glass border-white/[0.08] p-0 text-foreground shadow-2xl shadow-black/40 transition-[max-width,height] duration-200 ease-out '
    + 'sm:w-[calc(100%_-_2rem)] sm:max-h-[calc(100vh_-_2rem)] sm:rounded-2xl '
    + 'data-[state=open]:animate-dialog-content-show data-[state=closed]:animate-dialog-content-hide';
// Default centered card size (desktop). On mobile the base is already full-bleed.
const contentCardSizeClassName = 'sm:h-[560px] sm:max-w-2xl';
// Maximized: stretch to fill the viewport (1rem margin all round), keeping the rounded window look.
const contentFullSizeClassName = 'sm:h-[calc(100vh_-_2rem)] sm:max-w-[calc(100vw_-_2rem)]';
// Shared box for the header icon buttons so the maximize and close icons line up exactly
// (inline-flex centering on both, same top offset; only the right offset differs).
const iconButtonClassName = 'absolute top-4 z-30 inline-flex items-center justify-center rounded-sm opacity-70 '
    + 'ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-muted-foreground focus:ring-offset-2';
// Persist the maximize preference across sessions.
const EXPANDED_STORAGE_KEY = 'daredrop:accountSettingsExpanded';
export const ProfileSettingsDialog = ({ children, open, onOpenChange, initialTab }) => {
    var _a;
    const [activeTab, setActiveTab] = useState(initialTab !== null && initialTab !== void 0 ? initialTab : null);
    const [expanded, setExpanded] = useLocalStorage(EXPANDED_STORAGE_KEY, false);
    useEffect(() => {
        if (open && initialTab) {
            setActiveTab(initialTab);
        }
    }, [open, initialTab]);
    const { isViewer } = useIsViewer();
    const { isBrand, isBrandUser, isBrandAdmin, isEnterprise } = useIsBrand();
    const { isAgency } = useIsAgency();
    const { isAdmin } = useIsAdmin();
    const isPublisher = isBrand || isBrandUser || isBrandAdmin || isEnterprise;
    const rootProps = open !== undefined ? { open, onOpenChange } : {};
    const tabs = [
        isViewer && { key: 'channels', label: 'Channels', Icon: LinkIcon, Panel: ConnectedChannelsPanel },
        isViewer && { key: 'payments', label: 'Payments', Icon: CreditCardIcon, Panel: PaymentsPanel },
        { key: 'account', label: 'Account', Icon: PersonIcon, Panel: AccountPanel },
        isViewer && { key: 'platforms', label: 'Platforms', Icon: SportsEsportsIcon, Panel: GamePlatformsPanel },
        isViewer && { key: 'obs', label: 'OBS', Icon: VideocamIcon, Panel: OBSPanel },
        isPublisher && { key: 'publisher', label: 'Publisher', Icon: StorefrontIcon, Panel: PublisherPanel },
        isAgency && { key: 'agency', label: 'Agency', Icon: SupervisorAccountIcon, Panel: AgencyPanel },
        isAdmin && { key: 'admin', label: 'Admin', Icon: AdminPanelSettingsIcon, Panel: AdminPanel },
    ].filter((tab) => Boolean(tab));
    const currentTab = (_a = tabs.find(tab => tab.key === activeTab)) !== null && _a !== void 0 ? _a : tabs[0];
    const ActivePanel = currentTab === null || currentTab === void 0 ? void 0 : currentTab.Panel;
    // Keep the active tab centered in the mobile horizontal scroller (instant on first paint, smooth on
    // later tab changes) so it is never scrolled off-screen.
    const activeTabRef = useRef(null);
    const firstScrollRef = useRef(true);
    useEffect(() => {
        var _a;
        (_a = activeTabRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ inline: 'center', block: 'nearest', behavior: firstScrollRef.current ? 'auto' : 'smooth' });
        firstScrollRef.current = false;
    }, [activeTab]);
    return (<DialogPrimitive.Root {...rootProps}>
			{children && <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>}
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className={overlayClassName}/>
				<DialogPrimitive.Content className={cn(contentBaseClassName, expanded ? contentFullSizeClassName : contentCardSizeClassName)}>
					<div className="border-b border-solid border-white/[0.06] px-6 py-4">
						<DialogPrimitive.Title className="font-display text-base font-semibold">Account Settings</DialogPrimitive.Title>
						<DialogPrimitive.Description className="text-xs text-muted-foreground">
							Manage your account, channels, and payments.
						</DialogPrimitive.Description>
					</div>

					<div className="flex min-h-0 flex-1 flex-col sm:flex-row">
						{/* Tabs: a left rail on desktop, a horizontal-scroll bar pinned to the bottom on mobile
            (order-2) with larger, stacked icon + label touch targets (>= 48px) for the thumb. */}
						<div role="tablist" aria-label="Settings" className={cn('order-2 flex shrink-0 snap-x snap-mandatory gap-1 overflow-x-auto scrollbar-hide border-t border-solid border-white/[0.06] p-2', 
        // Edge fade so it reads as scrollable (mobile only; -webkit- for iOS Safari).
        '[mask-image:linear-gradient(to_right,transparent,#000_1.25rem,#000_calc(100%_-_1.25rem),transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,#000_1.25rem,#000_calc(100%_-_1.25rem),transparent)]', 'sm:order-none sm:w-48 sm:flex-col sm:gap-0.5 sm:snap-none sm:overflow-x-visible sm:border-r sm:border-t-0 sm:[mask-image:none] sm:[-webkit-mask-image:none]')}>
							{tabs.map((tab) => {
            const { Icon } = tab;
            const isActive = (currentTab === null || currentTab === void 0 ? void 0 : currentTab.key) === tab.key;
            return (<button key={tab.key} ref={isActive ? activeTabRef : undefined} type="button" role="tab" aria-selected={isActive} aria-label={tab.label} onClick={() => setActiveTab(tab.key)} className={cn('relative flex shrink-0 items-center rounded-lg transition-colors', 'min-w-[68px] snap-center flex-col justify-center gap-1 px-2 py-2', 'sm:w-full sm:min-w-0 sm:flex-row sm:justify-start sm:gap-2.5 sm:px-3 sm:py-2 sm:snap-align-none', isActive ? 'text-foreground' : 'text-muted-foreground hover:bg-white/[0.04] hover:text-foreground')}>
										{isActive && (<motion.div layoutId="profile-settings-tab" className="absolute inset-0 rounded-lg bg-white/[0.08]" transition={{ type: 'spring', bounce: 0.15, duration: 0.4 }}/>)}
										<Icon className={cn('relative z-10', isActive && 'text-neon-cyan')} sx={{ fontSize: 24, '@media (min-width:640px)': { fontSize: 16 } }}/>
										<span className="relative z-10 text-[11px] font-medium leading-none sm:text-xs">{tab.label}</span>
									</button>);
        })}
						</div>

						<div className="dark-scrollbar order-1 min-h-0 flex-1 overflow-y-auto p-6 sm:order-none">
							<AnimatePresence mode="wait">
								<motion.div key={currentTab === null || currentTab === void 0 ? void 0 : currentTab.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
									{ActivePanel && <ActivePanel />}
								</motion.div>
							</AnimatePresence>
						</div>
					</div>

					<TooltipProvider delayDuration={200}>
						<Tooltip>
							<TooltipTrigger asChild>
								<button type="button" onClick={() => setExpanded(!expanded)} aria-label={expanded ? 'Exit full screen' : 'Full screen'} className={cn(iconButtonClassName, 'right-12 hidden text-muted-foreground sm:inline-flex')}>
									{expanded ? <FullscreenExitIcon sx={{ fontSize: 18 }}/> : <FullscreenIcon sx={{ fontSize: 18 }}/>}
									<span className="sr-only">{expanded ? 'Exit full screen' : 'Full screen'}</span>
								</button>
							</TooltipTrigger>
							<TooltipContent>{expanded ? 'Exit full screen' : 'Full screen'}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
					<DialogPrimitive.Close className={cn(iconButtonClassName, 'right-4')}>
						<CloseIcon sx={{ fontSize: 18 }}/>
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>);
};
